/**
 * Module editor panel (AR-328).
 *
 * Same shell as the step editor so switching between a module and one of its
 * steps doesn't move the page around. System modules (Checkout) render locked.
 */
import React, { useState } from 'react';
import { Lock, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  ACTIVATION_PATHWAYS,
  ACTIVATION_PLANS,
  ACTIVATION_ROUTES,
  routeShort,
  type ActivationModule,
  type VisibilityRule,
} from '@/lib/activationBuilder';
import { FieldLabel, PanelSectionLabel } from './activationPrimitives';
import { VisibilityRuleBuilder } from './VisibilityRuleBuilder';

interface ActivationModuleEditorProps {
  module: ActivationModule;
  onCancel: () => void;
  onSave: () => void;
  onAddStep: () => void;
  onArchive: () => void;
}

const CheckChip: React.FC<{ label: string; checked: boolean; disabled?: boolean }> = ({
  label,
  checked,
  disabled,
}) => (
  <label
    className={cn(
      'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-[11.5px] font-medium',
      checked
        ? 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-900 dark:bg-orange-950/40 dark:text-orange-300'
        : 'border-gray-200 bg-white text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400',
      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
    )}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      readOnly
      className="h-3.5 w-3.5 shrink-0 accent-orange-500"
    />
    {label}
  </label>
);

export const ActivationModuleEditor: React.FC<ActivationModuleEditorProps> = ({
  module,
  onCancel,
  onSave,
  onAddStep,
  onArchive,
}) => {
  const locked = !!module.locked;
  const [miniTracker, setMiniTracker] = useState(!!module.miniTracker);
  const [archived, setArchived] = useState(!!module.archived);
  const [join, setJoin] = useState<'AND' | 'OR'>('AND');
  const [rules, setRules] = useState<VisibilityRule[]>(() => [
    { field: 'Route', operator: 'is one of', value: module.routes.map(routeShort).join(', ') },
  ]);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <div className="flex flex-wrap items-start gap-3 border-b border-gray-200 px-6 py-5 dark:border-gray-700">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[10px] text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              Module {module.order}
            </span>
            {locked && (
              <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-violet-700 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300">
                <Lock className="h-2.5 w-2.5" /> System / Locked
              </span>
            )}
          </div>
          <h3 className="truncate text-xl font-semibold text-gray-900 dark:text-gray-100">
            {module.name}
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!locked && (
            <Button variant="outline" size="sm" onClick={onAddStep}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add step from module
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onCancel}>
            {locked ? 'Close' : 'Cancel'}
          </Button>
          {!locked && (
            <Button size="sm" onClick={onSave} className="bg-orange-500 text-white hover:bg-orange-600">
              Save module
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-6 px-6 py-5">
        {locked && (
          <p className="rounded-xl bg-gray-100 px-4 py-3 text-xs leading-relaxed text-gray-600 dark:bg-gray-800 dark:text-gray-300">
            Checkout is a system module. Its steps stay in this order and can be read for context, but
            they can&apos;t be moved, edited or hidden.
          </p>
        )}

        <section>
          <PanelSectionLabel>Module</PanelSectionLabel>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              <FieldLabel>Name</FieldLabel>
              <Input defaultValue={module.name} disabled={locked} className="h-9 text-xs" />
            </div>
            <div>
              <FieldLabel>Nav-header label</FieldLabel>
              <Input defaultValue={module.navLabel} disabled={locked} className="h-9 text-xs" />
              <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                What the member sees in the activation header.
              </p>
            </div>
          </div>
          <div className="mt-4">
            <FieldLabel>Description</FieldLabel>
            <Textarea defaultValue={module.description} rows={2} disabled={locked} className="text-xs" />
          </div>
        </section>

        <section>
          <PanelSectionLabel>Visibility</PanelSectionLabel>
          <FieldLabel>Routes</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {ACTIVATION_ROUTES.map((route) => (
              <CheckChip
                key={route.code}
                label={route.label}
                checked={module.routes.includes(route.code)}
                disabled={locked}
              />
            ))}
          </div>

          <div className="mt-4">
            <FieldLabel>Pathways</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {ACTIVATION_PATHWAYS.map((pathway) => (
                <CheckChip key={pathway} label={pathway} checked disabled={locked} />
              ))}
            </div>
          </div>

          <div className="mt-4">
            <FieldLabel>Plan tiers</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {ACTIVATION_PLANS.map((plan) => (
                <CheckChip key={plan} label={plan} checked disabled={locked} />
              ))}
            </div>
          </div>
        </section>

        <VisibilityRuleBuilder
          rules={rules}
          join={join}
          onJoinChange={setJoin}
          onRulesChange={setRules}
          readOnly={locked}
        />

        <section>
          <PanelSectionLabel>Behaviour</PanelSectionLabel>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2.5 dark:border-gray-700 dark:bg-gray-900">
              <div>
                <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">Mini-tracker</p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Show this module&apos;s progress in the member header.
                </p>
              </div>
              <Switch
                checked={miniTracker}
                onCheckedChange={setMiniTracker}
                disabled={locked}
                aria-label="Mini-tracker"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2.5 dark:border-gray-700 dark:bg-gray-900">
              <div>
                <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">Archived</p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Moves the module and its {module.steps.length} steps to Archive, out of the live flow.
                </p>
              </div>
              <Switch
                checked={archived}
                onCheckedChange={(next) => {
                  setArchived(next);
                  if (next) onArchive();
                }}
                disabled={locked}
                aria-label="Archived"
              />
            </div>
          </div>
        </section>

        <div className="flex flex-wrap items-center gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onCancel}>
              {locked ? 'Close' : 'Cancel'}
            </Button>
            {!locked && (
              <Button size="sm" onClick={onSave} className="bg-orange-500 text-white hover:bg-orange-600">
                Save module
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivationModuleEditor;
